
import * as THREE from "../../libs/three.js/build/three.module.js";
import {EventDispatcher} from "../EventDispatcher.js";
import {MOUSE} from "../defines.js";
import {PipeVisual} from "./PipeVisual.js";
import {projectToScreen} from "./geometry/polylineGeometry.js";
import {rectFromDrag, pointInRect, segmentIntersectsRect, classifyVertexIndexRun} from "./geometry/screenSpaceSelection.js";
import {openRangeMenuForVisual} from "./VectorEditingMenu.js";

import {t} from "./i18n/index.js";
// Rectangle/box-selection for the 3D viewer (Ctrl + right-click + drag).
//
// Reuses src/utils/ScreenBoxSelectTool.js's own, already-shipped mechanism
// 1:1 for "own this drag gesture without navigation interfering": register
// as a viewer.inputHandler input listener with an `importance` higher than
// the navigation controls' (EarthControls, unset/default 0), and call
// `e.consume()` inside the synthesized 'drag' event so EarthControls' own
// drag handler (lower importance, dispatched afterwards - see
// InputHandler.getSortedListeners()) never runs for that same tick. Kept
// permanently registered (unlike ScreenBoxSelectTool's own per-gesture
// register/unregister) - importance only affects DISPATCH ORDER, not
// exclusivity, so a permanently-high importance is harmless: every OTHER
// drag (plain left-pan, plain right-orbit) simply passes through unconsumed
// every time, exactly as before this feature existed.
//
// Gesture ownership is decided ONCE, on the first 'drag' tick of a session,
// and frozen for the rest of it (no mid-drag mode switching):
//   - `e.drag.mouse === MOUSE.RIGHT` (the raw e.buttons bitmask, populated by
//     InputHandler.onMouseMove - the same check EarthControls' own drag
//     handler uses to distinguish orbit from pan)
//   - `dm.ctrlHeld` (DigitizationModule's existing global Ctrl-tracking,
//     already used everywhere to pause tool captures - reused here directly
//     rather than adding a second Ctrl-tracking mechanism)
//   - `dm.hintElement.style.display === 'none'` (DigitizationModule's
//     existing instruction-overlay visibility - EVERY tool capture and every
//     VectorEditingMenu ephemeral interactive flow (Flyt punkt, reference-
//     picking, Vælg sammenhængende del, Omtegn del) already shows this while
//     active, so checking it covers "nothing else currently owns input" in
//     one already-existing signal, without a new tracked flag)
//
// Deliberately NOT gated on `!dm.activeTool`: tools stay armed indefinitely
// once picked from the toolbar (see DigitizationModule's own doc comment on
// this), so that gate would in practice almost never be true. The REQUIRED
// Ctrl modifier already, via `isPaused()`, makes every tool-capture click
// handler a no-op for as long as Ctrl is held - there is no real capture-vs-
// box-select gesture conflict possible regardless of `activeTool`'s value.
const MIN_DRAG_PX = 5;

export class BoxSelection extends EventDispatcher{
	constructor(dm){
		super();
		this.dm = dm;
		// higher than EarthControls' default/unset importance (0) - see this
		// file's own header comment
		this.importance = 10;

		this.engaged = null; // null = undecided this gesture; true/false once decided, reset on drop
		this.overlay = null;
		this.label = null;

		this.addEventListener("drag", (e) => this._onDrag(e));
		this.addEventListener("drop", (e) => this._onDrop(e));

		dm.viewer.inputHandler.addInputListener(this);
	}

	_isGestureCandidate(e){
		return e.drag.mouse === MOUSE.RIGHT && this.dm.ctrlHeld && this.dm.hintElement.style.display === "none";
	}

	_onDrag(e){
		if(this.engaged === null){
			this.engaged = this._isGestureCandidate(e);
			if(this.engaged) this._createOverlay();
		}
		if(!this.engaged) return; // not our gesture - let EarthControls/etc handle this drag tick normally

		e.consume();
		this._updateOverlay(e.drag.start, e.drag.end);
	}

	_onDrop(e){
		if(this.engaged){
			this._finish(e.drag.start, e.drag.end);
		}
		this.engaged = null;
		this._removeOverlay();
	}

	// Called from DigitizationModule's single global ESC keydown listener,
	// BEFORE it calls cancelActiveTool() - returns true (and the caller
	// should stop there) only if a box-selection drag was actually in
	// progress, so cancelling it never also deactivates the user's armed
	// tool as an unrelated side effect.
	cancelIfActive(){
		if(this.engaged !== true) return false;
		this.engaged = null;
		this._removeOverlay();
		return true;
	}

	_createOverlay(){
		const domElement = this.dm.viewer.renderer.domElement;

		this.overlay = document.createElement("div");
		this.overlay.style.cssText = [
			"position: absolute", "pointer-events: none", "z-index: 9999", "box-sizing: border-box",
		].join(";");

		this.label = document.createElement("div");
		this.label.style.cssText = [
			"position: absolute", "top: -20px", "left: 0", "white-space: nowrap",
			"font-family: Arial, sans-serif", "font-size: 11px", "color: #fff",
			"background: rgba(0,0,0,0.6)", "padding: 1px 6px", "border-radius: 3px",
		].join(";");

		this.overlay.appendChild(this.label);
		domElement.parentElement.appendChild(this.overlay);
	}

	// Rectangle outline/fill directly foreshadows the highlight colour the
	// eventual selection will get (task section 11: no hidden rule without
	// feedback) - solid yellow (matches FeatureVisual's new
	// COLOR_HIGHLIGHT_HEX vertex highlight) for window mode, dashed orange
	// (matches COLOR_SELECTED) for crossing mode - also the same solid-vs-
	// dashed convention professional CAD tools already use for window vs.
	// crossing selection.
	_updateOverlay(start, end){
		const rect = rectFromDrag(start, end);
		this.overlay.style.left = `${rect.minX}px`;
		this.overlay.style.top = `${rect.minY}px`;
		this.overlay.style.width = `${rect.maxX - rect.minX}px`;
		this.overlay.style.height = `${rect.maxY - rect.minY}px`;

		if(rect.direction === "window"){
			this.overlay.style.border = "2px solid #ffdc00";
			this.overlay.style.background = "rgba(255,220,0,0.12)";
			this.label.textContent = t("Vindue: vertices");
		}else{
			this.overlay.style.border = "2px dashed #ffa500";
			this.overlay.style.background = "rgba(255,165,0,0.10)";
			this.label.textContent = t("Krydsning: objekter");
		}
	}

	_removeOverlay(){
		if(this.overlay && this.overlay.parentElement) this.overlay.parentElement.removeChild(this.overlay);
		this.overlay = null;
		this.label = null;
	}

	_finish(start, end){
		if(Math.hypot(end.x - start.x, end.y - start.y) < MIN_DRAG_PX) return; // near-stationary "drag" - not a real gesture

		const rect = rectFromDrag(start, end);
		const dm = this.dm;
		const camera = dm.viewer.scene.getActiveCamera();
		const size = dm.renderer.getSize(new THREE.Vector2());
		const width = size.width, height = size.height;

		// Occlusion/3D handling (task section 9): deliberately NO depth test
		// against the point cloud or other geometry - consistent with every
		// existing hit-test (_findHoveredSegment etc) already being purely
		// screen-space. The one real exclusion is a point actually behind
		// the CAMERA itself (view-space z >= 0) - projectToScreen() itself
		// never detects this (confirmed: it unconditionally divides by
		// whatever w the projection matrix produces), so it's checked here
		// explicitly, before projecting.
		const isInFront = (worldPos) => worldPos.clone().applyMatrix4(camera.matrixWorldInverse).z < 0;
		const project = (worldPos) => projectToScreen(worldPos, camera, width, height);

		dm.clearMultiSelection();

		const entries = rect.direction === "window"
			? this._collectWindowVertices(rect, isInFront, project)
			: this._collectCrossingFeatures(rect, isInFront, project);

		if(entries.length === 0) return;

		if(entries.length === 1){
			// A single-element result collapses into the existing,
			// already-proven single-selection mechanism rather than
			// standing up multiSelection state for a trivial case.
			dm.selectVisual(entries[0].visual);
			return;
		}

		if(rect.direction === "window"){
			const classified = this._classifyWindowEntries(entries);
			if(classified){
				if(classified.type === "full"){
					// every vertex of a single feature got caught by the window -
					// that IS whole-feature selection, not a distinct vertex-set.
					dm.selectVisual(classified.visual);
					return;
				}
				// a contiguous partial run on a single feature is exactly what
				// the existing 2-click "Vælg sammenhængende del" flow already
				// produces (see VectorEditingMenu.js's openRangeMenuForVisual doc
				// comment) - route there directly instead of a generic vertex
				// multi-selection with no dedicated operations of its own.
				const domRect = dm.viewer.renderer.domElement.getBoundingClientRect();
				const clientX = domRect.left + end.x;
				const clientY = domRect.top + end.y;
				openRangeMenuForVisual(dm, classified.visual, {start: classified.start, end: classified.end}, clientX, clientY);
				return;
			}

			dm.multiSelection = {scope: "vertex", entries};
			for(const entry of entries){
				if(entry.visual.setVertexHighlight) entry.visual.setVertexHighlight(entry.vertexIndex, true);
			}
		}else{
			dm.multiSelection = {scope: "feature", entries};
			for(const entry of entries) entry.visual.setSelected(true);
		}
	}

	// Only classifiable when every entry belongs to the SAME visual - a
	// window spanning multiple features has no single-feature range/full
	// meaning, so it always falls back to the generic vertex multi-selection.
	_classifyWindowEntries(entries){
		const visual = entries[0].visual;
		for(const entry of entries) if(entry.visual !== visual) return null;

		const indices = entries.map((e) => e.vertexIndex).sort((a, b) => a - b);
		const classification = classifyVertexIndexRun(indices, visual.points.length);
		if(!classification) return null;

		return {...classification, visual};
	}

	// Window mode (task section 6: "præcis... primært relevant til valg af
	// delgeometri... vertices"): every vertex, across every visible, in-
	// front-of-camera FeatureVisual, whose screen projection falls inside
	// the rectangle. PipeVisual has no vertex concept at all (`!visual.points`,
	// same discriminator _findHoveredVertex()/_findHoveredSegment() already
	// use) and never contributes here - it's crossing-mode/feature-scope
	// only (see _collectCrossingFeatures()).
	_collectWindowVertices(rect, isInFront, project){
		const entries = [];
		for(const visual of this.dm.visuals.values()){
			if(visual.visible === false) continue; // respects the sidebar's own per-feature visibility toggle - "skjulte features" (task section 9)
			if(!visual.points) continue;

			for(let i = 0; i < visual.points.length; i++){
				const worldPos = visual.points[i].position;
				if(!isInFront(worldPos)) continue;
				if(pointInRect(project(worldPos), rect)) entries.push({visual, vertexIndex: i});
			}
		}
		return entries;
	}

	// Crossing mode (task section 6: "løs... primært relevant til valg af
	// hele features... enten ligger inde i, eller krydser rectangle"): every
	// feature (any geometry type, including Rør) that is fully inside OR
	// crosses the rectangle boundary.
	_collectCrossingFeatures(rect, isInFront, project){
		const entries = [];
		for(const visual of this.dm.visuals.values()){
			if(visual.visible === false) continue;
			if(this._featureCrossesOrIsInsideRect(visual, rect, isInFront, project)) entries.push({visual});
		}
		return entries;
	}

	_featureCrossesOrIsInsideRect(visual, rect, isInFront, project){
		if(visual.points){
			const n = visual.points.length;
			if(n === 0) return false;

			const screenPoints = visual.points.map((p) => (isInFront(p.position) ? project(p.position) : null));
			for(const sp of screenPoints) if(sp && pointInRect(sp, rect)) return true;

			const segmentCount = visual.closed ? n : n - 1;
			for(let i = 0; i < segmentCount; i++){
				const a = screenPoints[i];
				const b = screenPoints[(i + 1) % n];
				// a segment with an endpoint behind the camera is skipped for
				// the crossing test itself (its own endpoint(s) were already
				// tested above via pointInRect when in front) - consistent
				// with this module's "no depth/behind-camera guessing" stance
				if(a && b && segmentIntersectsRect(a, b, rect)) return true;
			}
			return false;
		}

		if(visual instanceof PipeVisual){
			return this._pipeCrossesOrIsInsideRect(visual, rect, isInFront, project);
		}

		return false;
	}

	// A Rør has no vertex/segment concept (see PipeVisual.js) - tested via
	// the same two meshes _findHoveredWholeObject() already raycasts
	// against (cylinderMesh/stationMarker): project their world-space
	// bounding box corners to screen space, then a plain axis-aligned
	// rectangle-overlap test against the selection rect. Cheap, and correct
	// for all three cases a single "any corner inside" check would miss
	// (the rect fully inside the pipe's own screen footprint, or vice
	// versa, or a genuine partial overlap).
	_pipeCrossesOrIsInsideRect(visual, rect, isInFront, project){
		const box = new THREE.Box3();
		if(visual.cylinderMesh && visual.cylinderMesh.visible) box.expandByObject(visual.cylinderMesh);
		if(visual.stationMarker && visual.stationMarker.visible) box.expandByObject(visual.stationMarker);
		if(box.isEmpty()) return false;

		const corners = [
			new THREE.Vector3(box.min.x, box.min.y, box.min.z), new THREE.Vector3(box.max.x, box.min.y, box.min.z),
			new THREE.Vector3(box.min.x, box.max.y, box.min.z), new THREE.Vector3(box.max.x, box.max.y, box.min.z),
			new THREE.Vector3(box.min.x, box.min.y, box.max.z), new THREE.Vector3(box.max.x, box.min.y, box.max.z),
			new THREE.Vector3(box.min.x, box.max.y, box.max.z), new THREE.Vector3(box.max.x, box.max.y, box.max.z),
		];

		const projected = corners.filter((c) => isInFront(c)).map((c) => project(c));
		if(projected.length === 0) return false;

		const screenMinX = Math.min(...projected.map((p) => p.x));
		const screenMaxX = Math.max(...projected.map((p) => p.x));
		const screenMinY = Math.min(...projected.map((p) => p.y));
		const screenMaxY = Math.max(...projected.map((p) => p.y));

		return screenMinX <= rect.maxX && screenMaxX >= rect.minX && screenMinY <= rect.maxY && screenMaxY >= rect.minY;
	}
}
